import type { ReactNode } from "react"
import { CheckOutlined, CloseOutlined } from "@ant-design/icons"
import { Button, Flex, Typography } from "antd"
import { FIRST_VIDEO_GIFT_POINTS, VIDEO_TOOL_LABELS } from "~/common/constants"
import { useAnalyticsEvent } from "~/components/Analytics"
import { CrownIcon, UserFilledIcon } from "~/components/Icons"
import { Text } from "~/components/Text"
import { PROFESSIONAL_PLAN, PROFESSIONAL_UPGRADE } from "./professionalPlan"
import styles from "./index.css"

import { PolicyFooter, type PurchaseHandler } from "./shared"

const { Title } = Typography

type UpgradeFeature = {
  enabled: boolean
  title: string
  description: ReactNode
}

const freeFeatures: UpgradeFeature[] = [
  {
    enabled: false,
    title: "每月赠点",
    description: `新手注册送${FIRST_VIDEO_GIFT_POINTS}积点，后续无其他赠送`,
  },
  {
    enabled: true,
    title: "视频脚本拆解",
    description: "智能拆解视频结构化脚本",
  },
  {
    enabled: true,
    title: "视频复刻-微改编复刻",
    description:
      "保留参考视频的节奏、卖点结构，AI 根据新商品自适应调整场景、动作和画面表现，生成相似但不雷同的新视频",
  },
  {
    enabled: true,
    title: VIDEO_TOOL_LABELS.frameCopy,
    description: "高度还原场景、镜头和人物动作，仅替换商品，快速生成同款视频",
  },
  {
    enabled: false,
    title: "热门视频提示词",
    description:
      "持续更新热门视频模板提示词，全套含钩子、分镜、运镜、素材参考，一键复用，快速复刻同款视频",
  },
  {
    enabled: false,
    title: "充值优惠",
    description: (
      <>
        积点充值单价{" "}
        <Text
          color="primary"
          size="s"
        >
          ¥0.30/积点
        </Text>
      </>
    ),
  },
]

const professionalFeatures: UpgradeFeature[] = [
  {
    enabled: true,
    title: "每月赠点",
    description: (
      <>
        除新手注册增{FIRST_VIDEO_GIFT_POINTS}积点外，每月自动到账
        <Text
          color="primary"
          size="s"
        >
          {PROFESSIONAL_PLAN.points}积点
        </Text>
        ，折合
        <Text
          color="primary"
          size="s"
        >
          ¥0.23/积点
        </Text>
        ，订购当月有效
      </>
    ),
  },
  {
    enabled: true,
    title: "视频脚本拆解",
    description: "智能拆解视频结构化脚本",
  },
  {
    enabled: true,
    title: "视频复刻-微改编复刻",
    description:
      "保留参考视频的节奏、卖点结构，AI 根据新商品自适应调整场景、动作和画面表现，生成相似但不雷同的新视频",
  },
  {
    enabled: true,
    title: VIDEO_TOOL_LABELS.frameCopy,
    description: "高度还原场景、镜头和人物动作，仅替换商品，快速生成同款视频",
  },
  {
    enabled: true,
    title: "热门视频提示词",
    description:
      "持续更新热门视频模板提示词，全套含钩子、分镜、运镜、素材参考，一键复用，快速复刻同款视频",
  },
  {
    enabled: true,
    title: "充值优惠",
    description: (
      <>
        积点充值单价{" "}
        <Text
          color="primary"
          size="s"
        >
          ¥0.25/积点
        </Text>
      </>
    ),
  },
  {
    enabled: true,
    title: "专属客服1V1服务",
    description: "专属服务通道，快速响应问题",
  },
]

export const UpgradeContent = ({
  onPurchase,
}: {
  onPurchase: PurchaseHandler
}) => {
  const sendAnalytics = useAnalyticsEvent({
    category: "账户体系",
    prefix: "版本升级弹窗",
  })

  return (
    <Flex
      className={styles.upgradeContent}
      vertical
      gap={28}
    >
      <Flex
        className={styles.planCompare}
        gap={8}
      >
        <Flex
          className={styles.freePlanCard}
          vertical
        >
          <Text
            className={styles.planLabel}
            type="secondary"
            strong
          >
            当前版本
          </Text>
          <Flex
            vertical
            className={styles.content}
            gap={26}
          >
            <Flex
              align="center"
              className={styles.planHeader}
              justify="space-between"
            >
              <Flex
                align="center"
                gap={10}
              >
                <span className={styles.freeIcon}>
                  <UserFilledIcon />
                </span>
                <Text
                  size="l"
                  strong
                >
                  免费版
                </Text>
              </Flex>
              <Title
                level={4}
                className={styles.freePrice}
                type="secondary"
              >
                ¥0
              </Title>
            </Flex>
            <Flex
              className={styles.features}
              vertical
              gap={12}
            >
              {freeFeatures.map((feature) => (
                <FeatureItem
                  feature={feature}
                  key={feature.title}
                />
              ))}
            </Flex>
          </Flex>
        </Flex>

        <Flex
          className={styles.professionalPlanCard}
          vertical
        >
          <span className={styles.planLabel}>
            性价比首选，单价低至 ¥0.23/积点
          </span>
          <Flex
            className={styles.content}
            vertical
            gap={26}
          >
            <Flex
              align="center"
              className={styles.planHeader}
              justify="space-between"
              gap={12}
            >
              <Flex
                align="center"
                gap={10}
              >
                <span className={styles.planIcon}>
                  <CrownIcon fontSize={16} />
                </span>
                <Text
                  size="l"
                  strong
                >
                  专业版
                </Text>
              </Flex>
              <Title
                level={4}
                style={{ color: "var(--ant-color-primary)" }}
              >
                ¥{PROFESSIONAL_PLAN.price}
                <Text color="primary">/月</Text>
              </Title>
            </Flex>
            <Flex
              className={styles.features}
              vertical
              gap={12}
            >
              {professionalFeatures.map((feature) => (
                <FeatureItem
                  feature={feature}
                  key={feature.title}
                />
              ))}
            </Flex>
            <Button
              block
              className={styles.upgradeButton}
              onClick={() => {
                sendAnalytics("点击升级")
                onPurchase(PROFESSIONAL_UPGRADE)
              }}
              size="large"
              type="primary"
            >
              立即开通 ¥{PROFESSIONAL_PLAN.price}/月
            </Button>
          </Flex>
        </Flex>
      </Flex>
      <PolicyFooter />
    </Flex>
  )
}

type FeatureItemProps = {
  feature: UpgradeFeature
}

const FeatureItem = ({ feature }: FeatureItemProps) => {
  const disabled = !feature.enabled

  return (
    <Flex
      align="flex-start"
      className={disabled ? styles.featureDisabled : undefined}
      gap={10}
    >
      <span
        className={
          disabled ? styles.disabledFeatureIcon : styles.enabledFeatureIcon
        }
      >
        {disabled ? <CloseOutlined /> : <CheckOutlined />}
      </span>
      <Flex vertical>
        <Text
          size="m"
          strong
          type={disabled ? "secondary" : null}
        >
          {feature.title}
        </Text>
        <Text
          className={styles.featureDescription}
          size="s"
          type="secondary"
        >
          {feature.description}
        </Text>
      </Flex>
    </Flex>
  )
}
